import { Box, lighten, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Chart } from 'react-google-charts';
import { useTranslation } from 'react-i18next';
import { Colors } from 'src/shared/components/styles/colors';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getCountMembersByMonth } from 'src/shared/services/memberService';
import { ChartFailMessage } from '../atoms/chartFailMessage';
import { ChartInfoTooltip } from '../atoms/chartInfoTooltip';
import { HeaderSubTitle } from '../atoms/headerSubTitle';
import { MembersByMonth } from '../../models/memberByMonth';

type MemberChartProps = {
  associationId: number | undefined;
  districtId: number | undefined;
};

export const MemberChart = (props: MemberChartProps) => {
  const { associationId, districtId } = props;
  const { t } = useTranslation(['dashboard']);

  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [membersByMonth, setMembersByMonth] = useState<MembersByMonth[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getCountMembersByMonth(
          dayjs(new Date()).add(-12, 'month').startOf('month').format('YYYY-MM-DD'),
          dayjs(new Date()).format('YYYY-MM-DD'),
          associationId,
          districtId,
        );
        if (isMounted()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const tempArray: any[] = [];
          tempArray.push([t('dashboard:members.chart.monthYear'), t('dashboard:members.chart.paid'), t('dashboard:members.chart.unpaid')]);
          result.data.forEach((x) => {
            tempArray.push([`${x.month} ${x.year}`, x.paid, x.unpaid]);
          });
          setMembersByMonth(tempArray);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.error(e);
          setHasError(true);
        }
      } finally {
        setTimeout(() => {
          if (isMounted()) {
            setIsLoading(false);
          }
        }, 500);
      }
    };
    fetchData();
  }, [isMounted, t, associationId, districtId]);

  const getMaxValue = () => {
    const data = membersByMonth.slice(1); // Skip the header row
    let maxValue = 0;
    data.forEach((row) => {
      maxValue = Math.max(maxValue, row[1], row[2]); // Compare paid and unpaid values
    });
    return maxValue;
  };

  const maxValue = getMaxValue();

  const chartWidth = (maxValue > 10000 && '87%') || (maxValue > 1000 && '90%') || (maxValue < 1000 && '95%');

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <HeaderSubTitle label={t('dashboard:members.chartHeader')} />
        <ChartInfoTooltip />
      </Box>
      {isLoading && <Skeleton variant='rectangular' animation='wave' sx={{ height: '500px', borderRadius: 1 }} />}
      {membersByMonth && !isLoading && !hasError && (
        <Chart
          width='100%'
          height='500px'
          chartType='LineChart'
          data={membersByMonth}
          loader={<Skeleton variant='rectangular' animation='wave' sx={{ height: '500px' }} />}
          options={{
            chartArea: { width: chartWidth, height: '80%' },
            colors: [lighten(Colors.ChipColors.Green, 0.4), lighten(Colors.ChipColors.Red, 0.4)],
            legend: { position: 'top', maxLines: 3 },
            hAxis: {
              gridlines: { color: '#e0e0e0' },
            },
            animation: {
              startup: true,
              duration: 1000,
              easing: 'out',
            },
            vAxis: {
              gridlines: { color: '#e0e0e0' },
              format: '0',
            },
            pointSize: 8,
            lineWidth: 3, // Adjust line width as needed
            curveType: 'function', // Makes the lines smoother
            tooltip: { isHtml: true }, // Allows for richer tooltips
          }}
          rootProps={{ 'data-testid': '2' }}
        />
      )}
      {hasError && <ChartFailMessage />}
      {/* {isLoading && <LoadingChart />} */}
    </Box>
  );
};
